// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { Chart as ChartJS, Tooltip, ScriptableContext } from "chart.js";
import { Chart } from "react-chartjs-2";
import { SankeyController, Flow } from "chartjs-chart-sankey";

import { isSet } from "shared/utils/utils";

import ChartBaseComponent, {
  IChartBaseState,
  mapStateToProps,
  IChartTariffNode,
} from "./ChartBase";
import cssContent from "constants.module.scss";

ChartJS.register(SankeyController, Flow, Tooltip);

// zero flows are drawn with this share of the largest flow
const ZERO_FLOW_SHARE = 0.02;
// gap between nodes needed for a label, in px
const LABEL_GAP = 16;
// if all flows are zero, the node gaps are roughly this multiple of the flow width
const ZERO_NODE_PADDING = 20;

// SankeyDataPoint with additional fields
interface ISankeyData {
  from: string;
  to: string;
  flow: number;
  fromColor: string;
  toColor: string;
  tooltip: string;
}

interface ISankeyNode {
  key: string;
  name: string;
  color: string;
}

// chart width / height: the more flows, the less height per flow
function getAspectRatio(nrFlows: number) {
  const heightPerFlow =
    nrFlows < 4 ? 10
    : nrFlows < 10 ? 5
    : nrFlows < 15 ? 4
    : nrFlows < 25 ? 3
    : 2;
  return 100 / (nrFlows * heightPerFlow);
}

// gap between nodes as share of the chart height: big gaps keep the flows thin if all values
// are zero, otherwise the gaps should fit the node labels
function getNodePaddingShare(chartHeight: number, nrFlows: number, isAllZero: boolean) {
  if (isAllZero) {
    return ZERO_NODE_PADDING / nrFlows;
  }
  return LABEL_GAP / Math.max(chartHeight - nrFlows * LABEL_GAP, chartHeight / 2);
}

class SankeyChartComponent extends ChartBaseComponent<object, IChartBaseState> {
  private getChartData() {
    const tariffs = this.getSelectedTariffCircuits(
      this.props.isHierarchyViewMode === false, // show all tariffs when not in hierarchy mode,
      true, // show all categories in color
      false, // only color selected tariffs, not the whole hierarchy
    ).selectedTariffs;

    // zero flows are drawn as thin lines relative to the largest flow
    const maxValue = Math.max(0, ...tariffs.map((tariff) => Math.abs(tariff.value ?? 0)));

    // gray out tariffs not matching the search filter
    const filterText = this.props.filterText?.toLocaleLowerCase() ?? "";
    const getColor = (tariff: IChartTariffNode | undefined) =>
      tariff?.name.toLocaleLowerCase().includes(filterText) ?
        tariff.color
      : cssContent.GRAPH_GRAYED_OUT;

    // nodes are keyed by tariff id, the displayed labels are collected separately
    const labels: Record<string, string> = {};
    // labels of zero-flow nodes get a background, as the thin flow line would cross them
    const zeroLabelBackgrounds: Record<string, string> = {};

    const chartData = tariffs.map((tariff): ISankeyData => {
      const parent = tariffs.find((entry) => entry.id === tariff.parentId);
      // a parent outside of the selection (hierarchy mode) is shown by name only, without value
      const parentName =
        parent?.name ??
        this.props.tariffCircuits.find((tc, idx) => (tc.id ?? idx) === tariff.parentId)?.name;

      const node: ISankeyNode = {
        key: `tc_${tariff.id}`,
        name: tariff.name,
        color: getColor(tariff),
      };
      // tariffs without parent flow from a separate source node
      const parentNode: ISankeyNode = {
        key: isSet(parentName) ? `tc_${tariff.parentId}` : `src_${tariff.id}`,
        name: parentName ?? tariff.name,
        color: getColor(parent),
      };
      labels[node.key] = `${tariff.name}: ${tariff.valueText}`;
      labels[parentNode.key] =
        isSet(parent) ? `${parent.name}: ${parent.valueText}` : (parentName ?? labels[node.key]);

      const absValue = Math.abs(tariff.value ?? 0);
      if (absValue === 0) {
        zeroLabelBackgrounds[node.key] = "white";
        if (!isSet(parent)) {
          zeroLabelBackgrounds[parentNode.key] = "white";
        }
      }

      // negative values (e.g. feed-in) flow from the tariff to its parent
      const [from, to] = (tariff.value ?? 0) < 0 ? [node, parentNode] : [parentNode, node];
      return {
        from: from.key,
        to: to.key,
        flow:
          absValue > 0 ? absValue
          : maxValue > 0 ? maxValue * ZERO_FLOW_SHARE
          : 1,
        fromColor: from.color,
        toColor: to.color,
        tooltip: `${from.name} -> ${to.name}: ${tariff.valueText}`,
      };
    });

    return { chartData, labels, zeroLabelBackgrounds, isAllZero: maxValue === 0 };
  }

  public render() {
    // prepares allTariffCircuits, must be called in every render of a ChartBase component
    this.prepareHierarchyData();

    const { chartData, labels, zeroLabelBackgrounds, isAllZero } = this.getChartData();
    if (chartData.length === 0) {
      return null;
    }
    // node padding is scriptable, but typed as number in chartjs-chart-sankey
    const nodePadding = ((ctx: ScriptableContext<"sankey">) =>
      getNodePaddingShare(ctx.chart.height, chartData.length, isAllZero) *
      ctx.chart.canvas.height) as unknown as number;

    return (
      <div className="sankey">
        <Chart
          // re-create the chart on a changed number of flows, to apply the new aspect ratio
          key={chartData.length}
          type="sankey"
          data={{
            datasets: [
              {
                data: chartData,
                colorFrom: (ctx: any) => ctx.raw?.fromColor,
                colorTo: (ctx: any) => ctx.raw?.toColor,
                nodeWidth: 5,
                nodePadding,
                borderWidth: 0,
                size: "max",
                labels,
                nodeLabels: { backgroundColor: zeroLabelBackgrounds },
              },
            ],
          }}
          options={{
            animation: false,
            aspectRatio: getAspectRatio(chartData.length),
            // keep labels of nodes at the very top/bottom inside the chart
            layout: { padding: { top: 8, bottom: 8 } },
            plugins: {
              legend: { display: false },
              datalabels: { display: false },
              tooltip: {
                bodyColor: "#aaaaaa",
                backgroundColor: "#444444",
                callbacks: {
                  label: (tooltipItem) => chartData[tooltipItem.dataIndex].tooltip,
                },
              },
            },
          }}
        />
      </div>
    );
  }
}

export default connect(mapStateToProps)(SankeyChartComponent);
